import { selectArticleColoredViewerPolygons, selectArticleColoredStreetViewRenderer, selectArticleColoredLocal3dItems } from "../../ViewerRedux/articleColoredSceneSelectors";
import { COMPONENT_SEARCH_CONTEXT_OPACITY } from "../componentSearchAppearance";
import { selectSceneStreetViewSpatialItems, selectSceneStreetViewItemIds } from "../../ViewerRedux/viewerSceneSelectors";
import { VALIDATION_TILE_3D_ID_PREFIX } from "../../ViewerRedux/validationTile3dSelectors";
import { Box, Flex } from "@mantine/core";
import { mergeStreetViewRenderItems } from "../../ViewerRedux/streetViewRendering";
import { ML_HEATMAP_3D_ID_PREFIX } from "../../ViewerRedux/mlHeatmap3dSelectors";
import { CEILING_GRID_3D_ID_PREFIX } from "../../ViewerRedux/ceilingGrid3dSelectors";
import { PREVIEW_DEVICE_3D_ID_PREFIX, CEILING_DEVICE_3D_ID_PREFIX, selectStreetViewFloorElevationM } from "../../ViewerRedux/ceilingDevice3dSelectors";
import { clickOnDeviceThunk } from "../devices/deviceSlice";
import { selectHttpDevices } from "../devices/deviceSelectors";
import { skipToken } from "@reduxjs/toolkit/query";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { createCameraUpdateQueue } from "./cameraUpdateQueue";
import {
  useGetMcbimFragsQuery,
  useGetMcbimItemsUniqueClassesQuery,
  useGetMultiplacerDevicetypesQuery,
  openapi,
  type PostMcbimItems3DByIdsApiResponse,
} from "../../../openapi";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { usePrefetchMcBimItemsPerIfcClass } from "../../hooks/usePrefetchMcBimItemsPerIfcClass";
import { selectNrOfMcbimItems, selectnrOfMcbimSpaces } from "../../McBim/mcbimSelectors";
import { selectUniqueVisibleMcbimFileNames } from "../../McBim/extraMcBimSelectors";
import { selectProjectNoAndFloorName } from "../../McBim/McBimSlice";
import { selectProjectNo } from "../../Room/RoomSlice";
import {
  DesignLeftSideBarTabs,
  RIGHT_CLICK_DEVICE_MENU_ENUM,
} from "@multiconsult-group/multiplacer-core";
import { setActiveLeftSideBarTabInRedux, setHighlightedRoomNumberInRedux } from "../designSlice";
import { selectHighlightedRoomNumber } from "../roomHighlightSelectors";
import {
  dragSelectEndThunk,
  thunkClickOnAPixPolygon,
  thunkHandleRightMenu,
  thunkHandle3dObjectClick,
  selectObjectAndRoomThunk,
} from "../designThunk";
import {
  selectFloorElevation,
  selectModRotation,
  selectNextFloorElevation,
} from "../designSelector";
import {
  selectActiveDeviceToActiveRoomDimensionLines,
  selectFloorExtentKey,
  selectPixActionState,
  selectPixPaths,
  selectTransformPivot,
  selectViewerPixPolygons,
  selectMcBimSpacesPixPolygons,
  selectRoomNumberingHeatmapPaths,
  selectRoomNumberingHeatmapCircles,
  selectRoomNumberingRestrictionLinePaths,
  NUMBERING_ORIGIN_POLYGON_ID,
} from "../pixpolygonSelectors";
import {
  addRestrictionLine,
  selectRestrictionLine,
  setNumberingOrigin,
} from "../../RoomNumbering/roomNumberingSlice";
import { thunkMoveDeviceOnDragEnd, thunkStartMove } from "../devices/deviceThunks";
import { selectAutoPipeRoutingConnectionPointCircles } from "../routing/autoPipeRouting/autoPipeRoutingSelectors";
import { IS_DEV } from "../../config";
import { MultiViewer } from "@multiconsult-group/multiplacer-ui";
import type {
  IPixBimCameraState,
  MultiViewerStreetViewItem3d,
  MultiViewerStreetViewLoadArgs,
} from "@multiconsult-group/multiplacer-ui";
import {
  setCurrentViewerCameraInRedux,
} from "../../ViewerRedux/viewerSlice";

const readInitialCameraFromUrl = (): { zoom?: number; centerWorld?: { x: number; y: number } } => {
  const sp = new URLSearchParams(window.location.search);

  const z = sp.get("z") ?? sp.get("zoom");
  const zoom = z != null ? Number(z) : undefined;

  const p = sp.get("p") ?? sp.get("pan");
  if (p) {
    const [xRaw, yRaw] = p.split(",");
    const x = Number(xRaw);
    const y = Number(yRaw);
    if (Number.isFinite(x) && Number.isFinite(y)) {
      return { zoom, centerWorld: { x, y } };
    }
  }

  const x = Number(sp.get("x") ?? sp.get("cx"));
  const y = Number(sp.get("y") ?? sp.get("cy"));
  if (Number.isFinite(x) && Number.isFinite(y)) {
    return { zoom, centerWorld: { x, y } };
  }

  return { zoom };
};

const formatNum = (n: number) => String(Math.round(n * 1000) / 1000);
const URL_ROOM_HIGHLIGHT_MS = 3200;

const readInitialRoomFromUrl = (): string | null => {
  const sp = new URLSearchParams(window.location.search);
  return (sp.get("room") ?? sp.get("roomNumber"))?.trim() || null;
};

const hasStreetViewItemId = (
  item: PostMcbimItems3DByIdsApiResponse[number]
): item is PostMcbimItems3DByIdsApiResponse[number] & { _id: unknown } => item._id != null;

export const DesignTabViewer: React.FC<{
  onStreetViewActiveChange?: (active: boolean) => void;
}> = ({ onStreetViewActiveChange }) => {
  const dispatch = useAppDispatch();
  const initialRoomFromUrl = useMemo(readInitialRoomFromUrl, []);
  const restoredCamera = useAppSelector(state => state.viewer.restoredCamera);
  const currentFloorId = useAppSelector(state => state.design.floorAndJob?.floorGlobalId);
  const urlInitialCamera = useMemo(() => {
    const camera = readInitialCameraFromUrl();
    if (camera.zoom == null || camera.centerWorld == null) return undefined;
    return {
      zoom: camera.zoom,
      centerWorld: camera.centerWorld,
    };
  }, []);
  const initialCamera = restoredCamera
    ? restoredCamera.floorGlobalId === currentFloorId ? restoredCamera.camera : undefined
    : urlInitialCamera;
  const lastUrlCamRef = useRef("");
  const initialRoomHighlightScheduledRef = useRef<string | null>(null);
  const initialRoomHighlightTimeoutRef = useRef<number | null>(null);
  const highlightPolygon = useAppSelector(selectHighlightedRoomNumber);
  const projectNo = useAppSelector(selectProjectNo);
  const selectedRoomNumber = useAppSelector((state) => state.design.selectedSpaceNumber);
  const uniqueVisibleMcbimFileNames = useAppSelector(selectUniqueVisibleMcbimFileNames);
  const floorElevationMm = useAppSelector(selectFloorElevation);
  const nextFloorElevationMm = useAppSelector(selectNextFloorElevation);
  const clipZMm =
    floorElevationMm == null || nextFloorElevationMm == null
      ? null
      : { bottom: floorElevationMm, top: nextFloorElevationMm };

  useGetMultiplacerDevicetypesQuery({ projectNo });
  useGetMcbimItemsUniqueClassesQuery(useAppSelector(selectProjectNoAndFloorName) ?? skipToken);
  usePrefetchMcBimItemsPerIfcClass();

  const {
    data: fragData,
    error: fragFilesError,
    isFetching: isFetchingFragFiles,
    isLoading: isLoadingFragFiles,
  } = useGetMcbimFragsQuery(projectNo ? { projectNo } : skipToken);

  const pixPolygons = useAppSelector(selectViewerPixPolygons);
  const streetViewFloorName = useAppSelector(state => state.design.floorAndJob?.floorName);
  const streetViewRooms = useAppSelector(selectMcBimSpacesPixPolygons);
  const displayedPolygons = useAppSelector(selectArticleColoredViewerPolygons);
  const baseCircles = useAppSelector(selectAutoPipeRoutingConnectionPointCircles);
  const basePaths = useAppSelector(selectPixPaths);
  const dimensionLines = useAppSelector(selectActiveDeviceToActiveRoomDimensionLines);
  const heatmapPaths = useAppSelector(selectRoomNumberingHeatmapPaths);
  const heatmapCircles = useAppSelector(selectRoomNumberingHeatmapCircles);
  const streetViewSpatialItems = useAppSelector(selectSceneStreetViewSpatialItems);
  const local3dItems = useAppSelector(selectArticleColoredLocal3dItems);
  const visible3dItemIds = useAppSelector(selectSceneStreetViewItemIds);
  const streetViewFloorElevationM = useAppSelector(selectStreetViewFloorElevationM);
  const ceilingDevices = useAppSelector(selectHttpDevices);
  const renderStreetViewItem = useAppSelector(selectArticleColoredStreetViewRenderer);
  const renderStreetViewItems = useCallback(
    (items: MultiViewerStreetViewItem3d[], view: { center: [number, number] | null; radiusM: number }) =>
      mergeStreetViewRenderItems(items.filter(item => visible3dItemIds.has(String(item._id))).map(renderStreetViewItem), local3dItems, view),
    [renderStreetViewItem, visible3dItemIds, local3dItems]
  );
  const restrictionLinePaths = useAppSelector(selectRoomNumberingRestrictionLinePaths);
  const componentSearchActive = useAppSelector(state => state.viewer.freeTextSearch.activeSearch === "COMPONENT"
    && state.viewer.freeTextSearch.component.trim().length > 0);
  const dimContext = <T extends { opacity?: number }>(item: T): T => componentSearchActive
    ? { ...item, opacity: Math.min(item.opacity ?? 1, COMPONENT_SEARCH_CONTEXT_OPACITY) } : item;
  const pixPaths = [...basePaths, ...heatmapPaths, ...restrictionLinePaths].map(dimContext);
  const pixCircles = [...baseCircles, ...heatmapCircles].map(dimContext);
  const rotation = useAppSelector(selectModRotation);
  const transformPivot = useAppSelector(selectTransformPivot);
  const pixActionState = useAppSelector(selectPixActionState);
  const extentKey = useAppSelector(selectFloorExtentKey);
  const zoomExtentNonce = useAppSelector((state) => state.design.zoomExtentNonce);
  const zoomExtentKey = `${extentKey ?? "no-extent"}:${zoomExtentNonce}`;

  useEffect(() => {
    if (!initialRoomFromUrl) return;
    if (!selectedRoomNumber) return;
    if (selectedRoomNumber !== initialRoomFromUrl) return;
    if (initialRoomHighlightScheduledRef.current === initialRoomFromUrl) return;
    if (!pixPolygons.some((polygon) => polygon.id === initialRoomFromUrl)) return;

    initialRoomHighlightScheduledRef.current = initialRoomFromUrl;
    dispatch(setHighlightedRoomNumberInRedux(initialRoomFromUrl));
    initialRoomHighlightTimeoutRef.current = window.setTimeout(() => {
      dispatch(setHighlightedRoomNumberInRedux(null));
    }, URL_ROOM_HIGHLIGHT_MS);
  }, [dispatch, initialRoomFromUrl, pixPolygons, selectedRoomNumber]);

  useEffect(() => {
    return () => {
      if (initialRoomHighlightTimeoutRef.current != null) {
        window.clearTimeout(initialRoomHighlightTimeoutRef.current);
      }
      initialRoomHighlightScheduledRef.current = null;
      dispatch(setHighlightedRoomNumberInRedux(null));
    };
  }, [dispatch]);

  const publishCamera = useCallback((camera: IPixBimCameraState) => {
    if (camera.zoom == null || !camera.centerWorld) return;
    if (currentFloorId) {
      dispatch(setCurrentViewerCameraInRedux({
        floorGlobalId: currentFloorId,
        camera: { zoom: camera.zoom, centerWorld: camera.centerWorld },
      }));
    }

    // The shared-link lifecycle detaches the immutable state ID on a real view change.
    if (new URLSearchParams(window.location.search).has("state")) return;
    const z = formatNum(camera.zoom);
    const p = `${formatNum(camera.centerWorld.x)},${formatNum(camera.centerWorld.y)}`;
    const camKey = `z=${z}&p=${p}`;

    if (lastUrlCamRef.current === camKey) return;
    lastUrlCamRef.current = camKey;

    const sp = new URLSearchParams(window.location.search);
    sp.set("z", z);
    sp.set("p", p);
    sp.delete("zoom");
    sp.delete("pan");
    sp.delete("x");
    sp.delete("y");
    sp.delete("cx");
    sp.delete("cy");

    const newUrl = `${window.location.pathname}?${sp.toString()}${window.location.hash ?? ""}`;
    window.history.replaceState(null, "", newUrl);
  }, [dispatch, currentFloorId]);

  const cameraUpdates = useMemo(() => createCameraUpdateQueue(dispatch, publishCamera), [dispatch, publishCamera]);
  const lastQueuedCamera = useRef<{ floor?: string; zoom: number; x: number; y: number } | null>(null);
  useEffect(() => () => {
    cameraUpdates.cancel();
    lastQueuedCamera.current = null;
  }, [cameraUpdates]);
  const handleCameraChange = useCallback((camera: IPixBimCameraState) => {
    if (camera.zoom == null || !camera.centerWorld) return;
    const previous = lastQueuedCamera.current;
    const { x, y } = camera.centerWorld;
    if (previous && previous.floor === currentFloorId && previous.zoom === camera.zoom && previous.x === x && previous.y === y) return;
    lastQueuedCamera.current = { floor: currentFloorId, zoom: camera.zoom, x, y };
    cameraUpdates.push({ ...camera, centerWorld: { ...camera.centerWorld } });
  }, [cameraUpdates, currentFloorId]);

  const loadStreetViewItems = useCallback(
    async ({ itemIds }: MultiViewerStreetViewLoadArgs): Promise<MultiViewerStreetViewItem3d[]> => {
      if (!projectNo || itemIds.length === 0) return [];

      const items = (await dispatch(
        openapi.endpoints.postMcbimItems3DByIds.initiate(
          { body: { projectNo, ids: itemIds } },
          { forceRefetch: true, subscribe: false }
        )
      ).unwrap()) as PostMcbimItems3DByIdsApiResponse;

      return items.filter(hasStreetViewItemId);
    },
    [dispatch, projectNo]
  );

  return (
    <Box h="100%" w="100%" style={{ display: "flex", flexDirection: "column" }}>
      <StatsBar />
      <Box style={{ flex: 1, minHeight: 0 }}>
        <MultiViewer
          twoD={{
            polygons: displayedPolygons,
            circles: pixCircles,
            paths: pixPaths,
            dimensionLines,
            angle: rotation,
            action: pixActionState,
            zoomExtentKey,
            highlightPolygon,
            transformPivot,
            useLeftClickToPan: IS_DEV,
            flip2dCoordinate: true,
            initialCamera,
            onCameraChange: handleCameraChange,
            onMoveStart: (id) => {
              if (id !== NUMBERING_ORIGIN_POLYGON_ID) {
                dispatch(thunkStartMove(id));
              }
            },
            onRightMenuClick: (action, polygon) =>
              dispatch(thunkHandleRightMenu(action as RIGHT_CLICK_DEVICE_MENU_ENUM, polygon.id)),
            onClick: (id, coordinates) => {
              if (id?.startsWith("numbering-restriction-line-")) {
                dispatch(selectRestrictionLine(id.replace("numbering-restriction-line-", "")));
              } else if (id === NUMBERING_ORIGIN_POLYGON_ID) {
                dispatch(selectRestrictionLine(null));
              } else {
                dispatch(selectRestrictionLine(null));
                dispatch(thunkClickOnAPixPolygon(id, coordinates));
              }
            },
            onDrawPath: (start, end) => {
              dispatch(
                addRestrictionLine({
                  id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                  start,
                  end,
                })
              );
            },
            onDragAndDrop: (id, points) => {
              if (id === NUMBERING_ORIGIN_POLYGON_ID) {
                const pointCount = points.length;
                if (pointCount === 0) return;
                const [sumX, sumY] = points.reduce<[number, number]>(
                  ([accX, accY], point) => [accX + point[0]!, accY + point[1]!],
                  [0, 0]
                );
                dispatch(setNumberingOrigin([sumX / pointCount, sumY / pointCount]));
                return;
              }

              dispatch(thunkMoveDeviceOnDragEnd({ id, points }));
            },
            onDragSelected: (ids) => dispatch(dragSelectEndThunk(ids)),
          }}
          threeD={{
            fragFiles: fragData?.files ?? [],
            isLoadingFragFiles,
            isFetchingFragFiles,
            fragFilesError,
            visibleMcbimFileNames: uniqueVisibleMcbimFileNames,
            clipZMm,
            onObjectClick: (guid: string) => dispatch(thunkHandle3dObjectClick(guid)),
          }}
          boundingBox3d={{
            onItemClick: (item) => {
              const itemId = String(item?._id ?? "");
              if (itemId.startsWith(VALIDATION_TILE_3D_ID_PREFIX)) return;
              if (itemId.startsWith(PREVIEW_DEVICE_3D_ID_PREFIX)) return;
              if (itemId.startsWith(CEILING_GRID_3D_ID_PREFIX)) return;
              if (itemId.startsWith(ML_HEATMAP_3D_ID_PREFIX)) return;
              if (itemId.startsWith(CEILING_DEVICE_3D_ID_PREFIX)) {
                const device = ceilingDevices.find((d) => String(d._id) === itemId.slice(CEILING_DEVICE_3D_ID_PREFIX.length));
                if (device) dispatch(clickOnDeviceThunk(device));
                return;
              }
              dispatch(selectObjectAndRoomThunk(item?._id != null ? String(item._id) : null));
              if (item?._id == null) return;
              dispatch(setActiveLeftSideBarTabInRedux(DesignLeftSideBarTabs.MCBIMITEM_INFO));
            },
          }}
          streetView={{
            onActiveChange: onStreetViewActiveChange,
            entryPolygons: streetViewRooms,
            floorName: streetViewFloorName,
            initialCameraZ: streetViewFloorElevationM == null ? undefined : streetViewFloorElevationM + 1.65,
            preferredEntryPolygonId: selectedRoomNumber,
            spatialItems: streetViewSpatialItems,
            loadItems3d: loadStreetViewItems,
            renderItems3d: renderStreetViewItems,
          }}
        />
      </Box>
    </Box>
  );
};

const StatsBar: React.FC = () => {
  const floorAndJob = useAppSelector((state) => state.design.floorAndJob);
  const nrOfMcBimSpaces = useAppSelector(selectnrOfMcbimSpaces);
  const nrOfMcBimItems = useAppSelector(selectNrOfMcbimItems);

  if (floorAndJob == null) return null;

  return (
    <Flex justify="center" bg="white">
      <h6 style={{ margin: "0 10px" }}>Etasje: {floorAndJob.floorName}</h6>
      <h6 style={{ margin: "0 10px" }}>Rom: {nrOfMcBimSpaces}</h6>
      <h6 style={{ margin: "0 10px" }}>Komponenter: {nrOfMcBimItems}</h6>
    </Flex>
  );
};
